import { HttpErrorResponse, type HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, from, switchMap, throwError } from 'rxjs';

import { AuthService } from '../../auth/auth.service';
import { redirectToLoginAfterAuthFailure } from '../../auth/session-redirect';
import { IS_API_REQUEST } from '../api-context';

/**
 * Attach the current Auth0 access token to every API request. On a 401 we
 * try to refresh the token once; if that fails (or the retry is still a
 * 401) we clear the local session and send the user to `/login` with an
 * expiry notice so they can click through to Auth0 themselves.
 *
 * Must be the innermost interceptor so it sees raw 401s before the error
 * normaliser converts them. Mirrors the legacy `TokenInterceptor` in
 * growzz-web-app-legacy/app/interceptor.js.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.context.get(IS_API_REQUEST)) {
    return next(req);
  }

  const auth = inject(AuthService);
  const router = inject(Router);

  const token = auth.accessToken();
  const authed = token ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : req;

  return next(authed).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
        return throwError(() => error);
      }

      return from(auth.getFreshToken()).pipe(
        switchMap((fresh) => {
          if (!fresh) {
            void redirectToLoginAfterAuthFailure(auth, router);
            return throwError(() => error);
          }
          const retried = req.clone({
            setHeaders: { Authorization: `Bearer ${fresh}` },
          });
          return next(retried).pipe(
            catchError((retryError: unknown) => {
              if (retryError instanceof HttpErrorResponse && retryError.status === 401) {
                void redirectToLoginAfterAuthFailure(auth, router);
              }
              return throwError(() => retryError);
            }),
          );
        }),
      );
    }),
  );
};
